import { useContext } from 'react';
import {
  Button,
  Caption1,
  Caption1Strong,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  SelectTabData,
  SelectTabEvent,
  Tab,
  TabList,
  TabValue,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  AddRegular,
  ArrowSync16Regular,
  CheckmarkFilled,
  CloudCheckmark16Regular,
  EditOffRegular,
  EditRegular,
  OpenRegular,
  PeopleRegular,
} from '@fluentui/react-icons';
import { AccessType, TPermissions, useCanEdit } from '@modules/access';
import CommentsHeaderButton from '@modules/comments/components/CommentsHeaderButton';
import { TInlineBlock, TInlineBlockDraft, TInlineBlockVersion } from '../types';
import { ControlledTooltip, TOverflowButtonParam } from '@shared/components';

const useStyles = makeStyles({
  menuButton: {
    '& .fui-MenuButton__menuIcon': {
      fontSize: '15px',
      marginBottom: '6px',
    },
  },
});

type TProps = {
  inlineBlock: TInlineBlock;
  isEdit: boolean;
  isSaved: boolean;
  isError: boolean;
  handleEditDraft: () => void;
  handleSave: () => void;
  handleComplete: () => void;
  version: TInlineBlockVersion | TInlineBlockDraft | undefined;
  selectedTab: TabValue;
  setSelectedTab: (value: TabValue) => void;
  permissions: TPermissions;
  handleShowAccess: () => void;
};

const InlineBlockHeader = ({
  inlineBlock,
  isEdit,
  isSaved,
  isError,
  handleEditDraft,
  handleSave,
  handleComplete,
  version,
  selectedTab,
  setSelectedTab,
  permissions,
  handleShowAccess,
}: TProps) => {
  const { canEdit } = useCanEdit(permissions.accessRight);

  const onTabSelect = (_: SelectTabEvent, data: SelectTabData) => {
    if (data.value === 'file') return;
    setSelectedTab(data.value);
  };

  const menuItems: (TOverflowButtonParam & { show: boolean })[] = [
    {
      children: inlineBlock.draft ? 'Edit draft' : 'Create draft',
      icon: <EditRegular />,
      onClick: handleEditDraft,
      show: !isEdit && canEdit,
    },
    { children: 'Stop editing', icon: <EditOffRegular />, onClick: handleSave, show: isEdit },
    {
      children: 'Complete version',
      icon: <CheckmarkFilled />,
      onClick: handleComplete,
      show: Boolean(inlineBlock.draft),
    },
  ];

  const classes = useStyles();

  return (
    <header className="sticky z-40 w-full">
      <div>
        <div className="flex pb-2 justify-between items-end">
          <TabList selectedValue={selectedTab} onTabSelect={onTabSelect} size="small">
            <Tab value="home">Home</Tab>
            <Tab value="history">History</Tab>
          </TabList>

          <div className="flex space-x-2 items-center">
            <p className="mr-2 text-neutral-600">
              {isSaved ? (
                <Caption1>
                  <CloudCheckmark16Regular className="mb-1 mr-0.5" /> Saved
                </Caption1>
              ) : isError || (!isSaved && !isEdit) ? (
                <Caption1Strong className="text-red-500">
                  <ArrowSync16Regular className="mb-1 mr-0.5" /> Not saved. Please undo.
                </Caption1Strong>
              ) : (
                <Caption1>
                  <ArrowSync16Regular className="mb-1 mr-0.5" /> Saving...
                </Caption1>
              )}
            </p>

            {/* --- comment are currently not available for the customers --- */}
            {false && <CommentsHeaderButton item={inlineBlock} version={version} />}

            <Menu>
              <MenuTrigger disableButtonEnhancement>
                {isEdit ? (
                  <MenuButton size="small" icon={<EditRegular />} className={classes.menuButton}>
                    Editing
                  </MenuButton>
                ) : (
                  <MenuButton size="small" icon={<EditOffRegular />} className={classes.menuButton}>
                    Viewing
                  </MenuButton>
                )}
              </MenuTrigger>
              <MenuPopover>
                {canEdit ? (
                  <MenuList>
                    {menuItems.map(({ disabled, disabledMessage, ...menuItem }, idx) =>
                      menuItem.show ? (
                        <ControlledTooltip
                          key={idx}
                          relationship="inaccessible"
                          enable={disabled}
                          content={disabledMessage || ''}
                          withArrow={true}
                          positioning="before"
                        >
                          <MenuItem {...menuItem} disabled={disabled} />
                        </ControlledTooltip>
                      ) : null
                    )}
                  </MenuList>
                ) : (
                  <p className="p-1">No edit permission</p>
                )}
              </MenuPopover>
            </Menu>
            {permissions.accessRight >= AccessType.MANAGE && (
              <Button appearance="primary" size="small" icon={<PeopleRegular />} onClick={handleShowAccess}>
                Access
              </Button>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default InlineBlockHeader;
